import { useTranslation } from "react-i18next";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@radix-ui/react-collapsible";
import { useNavTranslation } from "@/hooks/useNavTranslation";
import { ChevronRight, Settings } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/utils/cn";
import { isActive, stripTrailingSlash } from "@/utils/helper";
import { preloadByUrl } from "@/utils/preloadByUrl";
import type { ModuleSettingsLink, NavTab } from "@/config/nav-tabs.config";
import type { INavItem } from "./sidebar.types";

interface INavAccordionItemProps {
  item: INavItem;
  tabs: NavTab[];
  /** Module's dedicated settings page — rendered as a trailing, separated row. */
  settingsLink?: ModuleSettingsLink;
  currentPath: string;
  currentSearch: string;
  onLinkClick: () => void;
  /** Whether this item's sub-tabs are expanded — owned by the parent so only one accordion item is open at a time. */
  isOpen: boolean;
  /**
   * Takes this item's own id, not just `open` — so the SAME stable function
   * can be passed for every row (see `Sidebar`'s `handleAccordionOpenChange`)
   * instead of a fresh per-item closure. A per-render closure here used to
   * change identity on every render of `Sidebar`, which made the auto-expand
   * effect below re-run on every render too (it depends on this callback's
   * identity) — and because that effect unconditionally reopens whenever the
   * module is still active, it silently undid a manual chevron collapse a
   * frame after it happened.
   */
  onOpenChange: (id: number, open: boolean) => void;
  /**
   * Overrides the URL prefix match on `item.url` — for a caller that knows
   * better which row the current page belongs to (the settings sidebar, whose
   * pages differ only by `?vTab=`).
   */
  active?: boolean;
  /** Overrides which child row is active: that tab's `to`, or null for none. */
  activeTab?: string | null;
  /**
   * Resolves a tab's label for display. Defaults to the `nav:tabs` slug lookup
   * module tabs use, which expects their plain-English labels.
   */
  translateLabel?: (label: string) => string;
}

/** Sentinel key for the trailing settings row in the match ranking below. */
const SETTINGS_KEY = "__module_settings__";

/**
 * How specifically `to` matches the current URL, or -1 for no match.
 *
 * Matching is a PREFIX match (so an in-module detail page such as
 * /company/teams/create still highlights the Teams row), which means several
 * rows can match at once — a row at the module ROOT matches everything below it.
 * The score is the matched path's length so the caller can light up only the
 * most specific match. Without that, opening /company/teams/settings lit up BOTH
 * "Teams" and "Team Settings".
 *
 * A few tabs (e.g. Onboarding's hTab tabs) share one path and differ only by
 * query string, so when `to` carries a "?" its params must match the current URL
 * too; such a row also outranks a bare path match on the same path.
 */
const matchScore = (currentPath: string, currentSearch: string, to: string) => {
  const [path, query] = to.split("?");
  if (!isActive(currentPath, path)) return -1;
  const length = stripTrailingSlash(path).length;
  if (!query) return length;
  const current = new URLSearchParams(currentSearch);
  const queryMatches = [...new URLSearchParams(query)].every(
    ([key, value]) => current.get(key) === value,
  );
  return queryMatches ? length + 1 : -1;
};

/**
 * Key of the single row that should render active — the most specific match
 * among the module's tabs and its settings row, or null when none match.
 */
const resolveActiveKey = (
  currentPath: string,
  currentSearch: string,
  tabs: NavTab[],
  settingsLink?: ModuleSettingsLink,
) => {
  const candidates: { key: string; to: string }[] = tabs.map((tab) => ({
    key: tab.to,
    to: tab.to,
  }));
  if (settingsLink) {
    candidates.push({ key: SETTINGS_KEY, to: settingsLink.to });
  }

  let activeKey: string | null = null;
  let best = -1;
  for (const candidate of candidates) {
    const score = matchScore(currentPath, currentSearch, candidate.to);
    if (score > best) {
      best = score;
      activeKey = candidate.key;
    }
  }
  return best < 0 ? null : activeKey;
};

/**
 * Expanded-only sidebar item that nests a module's page tabs as child links
 * (mirrors the admin sidebar's accordion). The row is split into two controls:
 *   - the label/icon is a plain <Link> that navigates to the module root, and
 *   - the chevron is a real <button> disclosure trigger that toggles the tree.
 * Keeping them separate avoids putting disclosure semantics on a navigating
 * anchor. The tree auto-expands whenever the current route is inside the module
 * (any tab OR an in-module detail/drill-down page), while the chevron still lets
 * the user collapse/expand manually. In the collapsed icon-rail the sidebar
 * renders a plain `NavItem` instead — there is no room for nested links there.
 */
export const NavAccordionItem = ({
  item,
  tabs,
  settingsLink,
  currentPath,
  currentSearch,
  onLinkClick,
  isOpen,
  onOpenChange,
  active,
  activeTab,
  translateLabel,
}: INavAccordionItemProps) => {
  const { t } = useTranslation(["common"]);
  const { translateTab } = useNavTranslation();
  // Prefix match on the module root → true for tabs AND in-module detail pages
  // (e.g. the employee drill-down /company/time-attendance/:id), matching the
  // old flat NavItem's highlight behaviour.
  const isModuleActive = active ?? isActive(currentPath, item.url);
  const tabLabel = translateLabel ?? translateTab;

  // Exactly one child row is active at a time — the most specific match.
  const activeKey =
    activeTab !== undefined
      ? activeTab
      : resolveActiveKey(currentPath, currentSearch, tabs, settingsLink);

  // Auto-expand when navigating into the module; this also closes any other
  // open item since open state is lifted to a single `openItemId` in the parent.
  useEffect(() => {
    if (isModuleActive) onOpenChange(item.id, true);
  }, [isModuleActive, item.id, onOpenChange]);

  /**
   * Self-close on a delay when the route LEAVES this module and it is still
   * the open one.
   *
   * This is the ONE case the effect above doesn't cover: navigating to a
   * plain leaf `NavItem` (Dashboard, Company Setup) fires no accordion's
   * "open" effect at all, so nothing ever told `openItemId` to clear and this
   * accordion stayed expanded after the route left it.
   *
   * Deferred a tick (rather than calling `onOpenChange(false)` directly in the
   * effect above) so it never races another accordion's OPEN in the same
   * commit: if the route moved from this module straight into a DIFFERENT
   * accordion, that accordion's own effect calls `onOpenChange(true)` in the
   * same batch, and effect order between sibling list items is unspecified —
   * a same-tick unconditional close here could fire after that open and undo
   * it. Deferring lets that open resolve and re-render first; by the time this
   * task runs, `isOpen` reflects the LATEST `openItemId` (via the prop), so it
   * only closes if this module is still the one holding it.
   *
   * Only on the transition out, never merely because the row is open while
   * its module is not the current page: that is exactly the state a manual
   * chevron expand produces, and closing it then snapped every row the user
   * opened by hand shut again a tick later.
   */
  const wasModuleActive = useRef(isModuleActive);
  useEffect(() => {
    const leftModule = wasModuleActive.current && !isModuleActive;
    wasModuleActive.current = isModuleActive;
    if (!leftModule || !isOpen) return;
    const timeoutId = setTimeout(() => onOpenChange(item.id, false), 0);
    return () => clearTimeout(timeoutId);
  }, [isModuleActive, isOpen, item.id, onOpenChange]);

  // Radix's `Collapsible` calls its `onOpenChange` with just a boolean — bind
  // this item's id so it reaches the parent's single id-aware handler as the
  // (id, open) pair every other caller here also uses. This is the manual
  // chevron toggle's own path — the one a user clicks to expand/collapse by
  // hand.
  const handleCollapsibleOpenChange = (open: boolean) =>
    onOpenChange(item.id, open);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleCollapsibleOpenChange}
      className="w-full"
    >
      <div
        className={cn(
          "group relative flex items-center rounded-xs overflow-hidden",
          "gap-1 ps-1 pe-1 lg:ps-1.5 lg:pe-1.5 py-1",
          isModuleActive
            ? "text-primary"
            : "text-foreground hover:bg-primary/10 hover:text-primary transition-colors duration-150",
        )}
      >
        <Link
          to={item.url}
          onClick={onLinkClick}
          onMouseEnter={() => preloadByUrl(item.url)}
          onFocus={() => preloadByUrl(item.url)}
          aria-current={isModuleActive ? "page" : undefined}
          className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0 rounded-xs outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        >
          <span className="shrink-0 flex items-center justify-center rounded-xs p-1 w-7 h-7 lg:w-8 lg:h-8 bg-primary/10 text-primary">
            {item.icon}
          </span>
          <span
            className={cn(
              "text-xs lg:text-sm font-semibold truncate flex-1 min-w-0 text-start",
              isModuleActive ? "text-primary" : "text-foreground",
            )}
          >
            {item.name}
          </span>
        </Link>

        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-label={
              isOpen
                ? t("common:chrome.collapse_section", { name: item.name })
                : t("common:chrome.expand_section", { name: item.name })
            }
            className="shrink-0 flex items-center justify-center rounded-xs p-1 size-6 text-muted-foreground hover:bg-primary/10 hover:text-primary outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ChevronRight
              className={cn(
                // Collapsed it points along the reading direction, so it must
                // mirror in RTL; expanded, `rotate-90` already points it down.
                "size-4 transition-transform duration-200",
                isOpen ? "rotate-90" : "rtl-flip",
              )}
            />
          </button>
        </CollapsibleTrigger>
      </div>

      {/* Slides + fades open and shut — see `.nav-collapsible` in global.css. */}
      <CollapsibleContent className="nav-collapsible overflow-hidden">
        <ul className="mt-0.5 ms-4 lg:ms-5 flex flex-col gap-0.5 border-s border-border ps-2 lg:ps-3">
          {tabs.map((tab) => {
            const isTabActive = activeKey === tab.to;
            return (
              <li key={tab.to}>
                <Link
                  to={tab.to}
                  onClick={onLinkClick}
                  onMouseEnter={() => preloadByUrl(tab.to)}
                  onFocus={() => preloadByUrl(tab.to)}
                  aria-current={isTabActive ? "page" : undefined}
                  className={cn(
                    "flex items-center rounded-xs px-2 lg:px-3 py-1.5",
                    "text-xs font-medium transition-colors duration-150",
                    isTabActive
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground hover:bg-primary/5 hover:text-primary",
                  )}
                >
                  <span className="truncate">{tabLabel(tab.label)}</span>
                </Link>
              </li>
            );
          })}

          {settingsLink && (
            <li
              className={cn(
                tabs.length > 0 && "mt-1 pt-1 border-t border-border",
              )}
            >
              <Link
                to={settingsLink.to}
                onClick={onLinkClick}
                onMouseEnter={() => preloadByUrl(settingsLink.to)}
                onFocus={() => preloadByUrl(settingsLink.to)}
                aria-current={activeKey === SETTINGS_KEY ? "page" : undefined}
                className={cn(
                  "flex items-center gap-1.5 rounded-xs px-2 lg:px-3 py-1.5",
                  "text-xs font-medium transition-colors duration-150",
                  activeKey === SETTINGS_KEY
                    ? "bg-primary/10 text-primary font-semibold"
                    : "text-muted-foreground hover:bg-primary/5 hover:text-primary",
                )}
              >
                <Settings className="size-3.5 shrink-0" />
                {/* Always plain "Settings" — the row already sits nested under
                    its module, so `settingsLink.label` ("Time & Attendance
                    Settings") would repeat the module name. The full label is
                    still used for the page CTA's tooltip, where there is no
                    parent to give it context. */}
                <span className="truncate">
                  {translateTab(t("nav:modules.settings"))}
                </span>
              </Link>
            </li>
          )}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};
